// Nouvelle conversation (charte D-038) : conversation individuelle avec une personne joignable (même
// organisation, relation déclarée ou objet commun ; jamais un autre distributeur), groupe interne à son
// organisation, annonce institutionnelle (ministère et DGH). Les personnes, les organisations et les droits
// d'émission sont fournis par le serveur, qui contrôle de nouveau chaque création.

import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MessageErreur, Squelette } from '../../composants/Etats';
import { cles, collegues, contacts, creerConversation, lireStatut, type Contact } from './api';
import { IconeH2 } from './icones';

type Type = 'individuelle' | 'groupe' | 'annonces';

function grouperParOrganisation(liste: Contact[]): { organisation: string; personnes: Contact[] }[] {
  const groupes = new Map<string, Contact[]>();
  for (const p of liste) groupes.set(p.organisation, [...(groupes.get(p.organisation) ?? []), p]);
  return [...groupes.entries()].map(([organisation, personnes]) => ({ organisation, personnes }));
}

export function NouvelleConversation() {
  const naviguer = useNavigate();
  const client = useQueryClient();
  const statut = useQuery({ queryKey: cles.statut, queryFn: lireStatut });
  const emission = statut.data?.emissionAnnonces ?? null;
  const [type, setType] = useState<Type>('individuelle');
  const [q, setQ] = useState('');
  const [recherche, setRecherche] = useState('');
  const [titre, setTitre] = useState('');
  const [choisis, setChoisis] = useState<string[]>([]);
  const [types, setTypes] = useState<string[]>([]);
  const [emettrice, setEmettrice] = useState('');
  const [contenu, setContenu] = useState('');
  // La recherche part un quart de seconde après la dernière frappe.
  useEffect(() => {
    const minuterie = setTimeout(() => setRecherche(q.trim()), 250);
    return () => clearTimeout(minuterie);
  }, [q]);
  const listeContacts = useQuery({
    queryKey: ['messagerie', 'contacts', recherche],
    queryFn: () => contacts(recherche),
    enabled: type === 'individuelle',
  });
  const listeCollegues = useQuery({
    queryKey: ['messagerie', 'collegues'],
    queryFn: collegues,
    enabled: type === 'groupe',
  });
  const groupes = useMemo(() => grouperParOrganisation(listeContacts.data ?? []), [listeContacts.data]);
  const creation = useMutation({
    mutationFn: (corps: Record<string, unknown>) => creerConversation(corps),
    onSuccess: async (c) => {
      await client.invalidateQueries({ queryKey: ['messagerie', 'conversations'] });
      naviguer(`/messages/${c.id}`);
    },
  });
  const basculer = (liste: string[], v: string) =>
    liste.includes(v) ? liste.filter((x) => x !== v) : [...liste, v];
  const choisirType = (t: Type) => {
    setType(t);
    setTitre('');
    creation.reset();
  };

  const soumettreGroupe = (e: FormEvent) => {
    e.preventDefault();
    if (titre.trim() && choisis.length > 0)
      creation.mutate({ type: 'groupe', titre: titre.trim(), participantIds: choisis });
  };
  const soumettreAnnonce = (e: FormEvent) => {
    e.preventDefault();
    if (titre.trim() && types.length > 0 && contenu.trim()) {
      creation.mutate({
        type: 'annonces',
        titre: titre.trim(),
        typesOrganisations: types,
        contenu: contenu.trim(),
        organisationEmettriceId: emettrice || emission?.organisations[0]?.id,
      });
    }
  };

  return (
    <section className="msg-fil msg-nouvelle" aria-labelledby="titre-nouvelle">
      <header className="msg-entete">
        <Link to="/messages" className="bouton discret msg-retour">
          Conversations
        </Link>
        <div className="msg-entete-texte">
          <span className="sur-titre">Messagerie</span>
          <h2 id="titre-nouvelle">Nouvelle conversation</h2>
          <p className="contexte">
            Deux distributeurs ne peuvent échanger que dans un canal de crise. Les canaux de service s’ouvrent
            automatiquement avec les livraisons, alertes, inspections, incidents, rapprochements et
            cargaisons.
          </p>
        </div>
        <div className="actions msg-entete-actions">
          <Link to="/messages" className="bouton">
            Annuler
          </Link>
        </div>
      </header>

      <div className="msg-defilement">
        <div className="segments" role="radiogroup" aria-label="Type de conversation">
          <button
            type="button"
            role="radio"
            aria-checked={type === 'individuelle'}
            onClick={() => choisirType('individuelle')}
          >
            Individuelle
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={type === 'groupe'}
            onClick={() => choisirType('groupe')}
          >
            Groupe interne
          </button>
          {emission && (
            <button
              type="button"
              role="radio"
              aria-checked={type === 'annonces'}
              onClick={() => choisirType('annonces')}
            >
              Annonce institutionnelle
            </button>
          )}
        </div>

        {type === 'individuelle' && (
          <div className="msg-nouvelle-corps">
            <p className="attenue">
              Personnes de votre organisation ou d’organisations en relation déclarée (contrat de transport,
              dépôt partagé, réseau de stations) ou partageant un objet commun.
            </p>
            <div className="msg-champ-recherche">
              <IconeH2 nom="recherche" />
              <label htmlFor="contact-q" className="visuellement-cache">
                Rechercher une personne
              </label>
              <input
                id="contact-q"
                type="search"
                placeholder="Rechercher une personne par son nom"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>
            {listeContacts.isLoading && <Squelette lignes={3} libelle="Recherche des personnes joignables" />}
            {listeContacts.error && <MessageErreur erreur={listeContacts.error} titre="Liste indisponible" />}
            {listeContacts.data && listeContacts.data.length === 0 && (
              <p className="attenue">Aucune personne joignable{recherche ? ` pour « ${recherche} »` : ''}.</p>
            )}
            {groupes.map((g) => (
              <div key={g.organisation} className="msg-contacts-groupe">
                <h3 className="sur-titre">{g.organisation}</h3>
                <ul className="msg-contacts">
                  {g.personnes.map((p) => (
                    <li key={p.id}>
                      <button
                        type="button"
                        disabled={creation.isPending}
                        onClick={() => creation.mutate({ type: 'individuelle', destinataireId: p.id })}
                      >
                        <span className="initiales" aria-hidden="true">
                          {`${p.prenom.charAt(0)}${p.nom.charAt(0)}`.toUpperCase()}
                        </span>
                        <span className="msg-contact-texte">
                          <b>
                            {p.prenom} {p.nom}
                          </b>
                          <small>{p.fonction ?? 'Fonction non renseignée'}</small>
                        </span>
                        <span className="contexte">Écrire</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}

        {type === 'groupe' && (
          <form className="msg-nouvelle-corps" onSubmit={soumettreGroupe}>
            <div className="champ">
              <label htmlFor="groupe-titre">Nom du groupe</label>
              <input
                id="groupe-titre"
                value={titre}
                onChange={(e) => setTitre(e.target.value)}
                maxLength={200}
                required
              />
            </div>
            <fieldset className="champ msg-fieldset">
              <legend>Membres de votre organisation</legend>
              {listeCollegues.isLoading && <Squelette lignes={3} libelle="Chargement des membres" />}
              {listeCollegues.error && (
                <MessageErreur erreur={listeCollegues.error} titre="Liste indisponible" />
              )}
              {listeCollegues.data && listeCollegues.data.length === 0 && (
                <p className="attenue">Aucun autre membre de votre organisation n’a accès à la messagerie.</p>
              )}
              <div className="msg-cases">
                {listeCollegues.data?.map((p) => (
                  <label key={p.id} className="case">
                    <input
                      type="checkbox"
                      checked={choisis.includes(p.id)}
                      onChange={() => setChoisis((l) => basculer(l, p.id))}
                    />
                    <span>
                      {p.prenom} {p.nom}
                      <small>{p.fonction ?? 'Fonction non renseignée'}</small>
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>
            <div className="actions">
              <button
                type="submit"
                className="bouton primaire"
                disabled={!titre.trim() || choisis.length === 0 || creation.isPending}
              >
                Créer le groupe
              </button>
              <span className="contexte">
                {choisis.length} membre{choisis.length > 1 ? 's' : ''} choisi{choisis.length > 1 ? 's' : ''},
                vous serez l’animateur.
              </span>
            </div>
          </form>
        )}

        {type === 'annonces' && emission && (
          <form className="msg-nouvelle-corps" onSubmit={soumettreAnnonce}>
            <p className="attenue">
              Canal en lecture pour les destinataires : seule votre organisation y écrit. Les accusés de
              lecture restent réservés aux émetteurs.
            </p>
            {emission.organisations.length > 1 && (
              <div className="champ">
                <label htmlFor="annonce-emettrice">Organisation émettrice</label>
                <select
                  id="annonce-emettrice"
                  value={emettrice || emission.organisations[0]!.id}
                  onChange={(e) => setEmettrice(e.target.value)}
                >
                  {emission.organisations.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.nom}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="champ">
              <label htmlFor="annonce-titre">Objet de l’annonce</label>
              <input
                id="annonce-titre"
                value={titre}
                onChange={(e) => setTitre(e.target.value)}
                maxLength={200}
                required
              />
            </div>
            <fieldset className="champ msg-fieldset">
              <legend>Organisations destinataires</legend>
              <div className="msg-cases">
                {emission.typesDestinataires.map((t) => (
                  <label key={t.code} className="case">
                    <input
                      type="checkbox"
                      checked={types.includes(t.code)}
                      onChange={() => setTypes((l) => basculer(l, t.code))}
                    />
                    <span>
                      {t.libelle}
                      <small>
                        {t.nombre} organisation{t.nombre > 1 ? 's' : ''}
                      </small>
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>
            <div className="champ">
              <label htmlFor="annonce-contenu">Message</label>
              <textarea
                id="annonce-contenu"
                value={contenu}
                onChange={(e) => setContenu(e.target.value)}
                maxLength={10000}
                required
              />
            </div>
            <div className="actions">
              <button
                type="submit"
                className="bouton primaire"
                disabled={!titre.trim() || types.length === 0 || !contenu.trim() || creation.isPending}
              >
                Publier l’annonce
              </button>
            </div>
          </form>
        )}
        {creation.error && <MessageErreur erreur={creation.error} titre="Création refusée" />}
      </div>
    </section>
  );
}
